import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { api, useAuth } from '@/context/AuthContext';
import { BACKEND_URL as APP_BACKEND_URL } from '@/lib/backendUrl';

const API_URL = APP_BACKEND_URL;

const TranslationContext = createContext(null);

// All translatable UI strings across the app. Organized by area.
const UI_STRINGS = [
  // Navbar
  'Dashboard', 'Assessments', 'Insights', 'Therapy', 'Partners', 'Connect', 'Community',
  'Profile', 'Name Analysis', 'Rankings', 'Plans', 'Content Manager', 'Notifications', 'Sign out',
  // Dashboard
  'Good morning', 'Good afternoon', 'Good evening',
  'Your journey so far', 'Mood check-in', 'How are you feeling today?',
  'Struggling', 'Low', 'Okay', 'Good', 'Thriving',
  "Today's Reflection", 'Save reflection', 'Saving...', 'Start your assessments',
  'assessments completed', 'Current streak', 'days', 'Insights for you',
  'Your Strongest Traits', 'Quick Actions', 'Continue', 'Recent',
  'Complete an assessment to see your traits here', 'Take your first assessment', 'No results yet',
  'Chat with your personal guide', "Discover your name's meaning", 'Exercises & journaling',
  'AI Guide', 'View all', 'Generating...',
  // Assessments
  'Assessment Hub', 'Scientific self-discovery', 'Choose an assessment to learn more about yourself.',
  'Free', 'Upgrade', 'Start', 'Resume', 'View Results', 'Completed', 'questions',
  'Unlock all assessments', 'Upgrade',
  // Therapy Hub
  'Therapy & Coaching Hub', 'Find the right support',
  'Self-discovery sometimes uncovers things you need to talk through. Here\'s everything you need to connect with the right professional.',
  'How It Works', 'Browse our team below', 'Look through our International Therapy Team and coaches to find someone who fits your needs.',
  'No match? Check external directories', 'We link to trusted resources like Psychology Today, BetterHelp, and affordable options like Open Path.',
  'Prepare a Session Brief', 'Before your first session, generate an AI-powered brief from your assessments, mood, and reflections. It saves time and gives your therapist a head start.',
  'Share securely', 'You control what gets shared. Send the brief only to the therapist you choose, with your explicit permission.',
  'Need support right now?', 'Try our AI Therapeutic Chat for 24/7 evidence-based support using CBT, ACT, and mindfulness techniques.',
  'International Therapy Team', 'Life Coaches', 'External Directories',
  'If our team isn\'t the right fit, these trusted resources can help you find someone.',
  'Not sure where to start?', 'Our AI guide knows everything on this page. Ask about therapists, how the session brief works, which option is best for your situation, or anything else.',
  'Need help choosing? Ask our AI Guide', 'Tools & Resources',
  'AI Therapeutic Chat', 'Available 24/7', 'Session Preparation Brief', 'Share with your therapist',
  'No in-house therapists yet', 'Add your first therapist above', 'Accepting clients', 'Waitlist',
  // Chat / Community
  'Support rooms', 'Join conversations with others on similar journeys. All rooms are monitored for a safe, supportive environment.',
  'messages', 'Members-Only Communities', 'Unlock', 'No messages yet. Start the conversation.',
  'Share your thoughts...', 'Chat Room', 'Send',
  // Partner Discovery
  'Partner Discovery', 'Find your match',
  'Discover people who complement you, based on the science of personality, attachment, values, and love languages.',
  'Included in paid plans', 'Partner Discovery is included in every paid plan.',
  'Your Discovery Profile', 'Edit Profile', 'About you', 'What you\'re looking for',
  'Active on discovery', 'Save profile', 'Your Matches', 'Overall compatibility',
  'View compatibility details', 'No matches yet',
  // Connections
  'Connections', 'We are all connected',
  'When you\'re ready, share parts of your journey with others.',
  'Your journey so far', 'What you share', 'Invite someone', 'Join someone',
  'Generate invite', 'Enter invite code', 'Join', 'Your connections', 'View',
  'You control exactly what others can see. Nothing is shared by default.',
  'Generate a code to share with someone you trust.',
  'Enter an invite code from someone who wants to connect.',
  // Profile
  'Your Profile', 'Edit profile', 'Save', 'Cancel', 'Preferred Language',
  'Content in your Insights feed can be translated to this language',
  'Display Name', 'Preferred Name', 'Bio', 'Tell us about yourself',
  'Sex', 'Gender', 'Demographic info helps us personalize your content',
  // Common
  'Loading...', 'Error', 'Try again', 'Back', 'Next', 'Submit', 'Close', 'Search',
  'Welcome back', 'Sign in', 'Sign up', 'Email', 'Password',
  // Insights
  'Curated For You', 'Insights for you',
  'Personalized resources based on your assessments, mood patterns, and journey.',
  'Translate', 'Original', 'Helpful', 'Not helpful', 'Was this helpful?',
  'Nothing here yet', 'Complete some assessments and log your mood to start receiving personalized content.',
  'Why am I seeing this?', 'Read more', 'Translated', 'Removed from your feed',
  // Plans
  'Sahiri Plans', 'Individual', 'Couple', 'Family', 'Save 2 months when billed annually', 'Monthly', 'Annual',
  'Upgrade your journey', 'Choose the plan that works for you.',
  'Free Plan', 'Your current plan', 'Current plan', 'Subscribe',
  "You're on the", 'plan', 'Thank you for being a member.',
  // Wellness
  'Wellness Toolkit', 'Tools for everyday wellbeing',
  'Exercises', 'Journal', 'Resources',
  'Breathing exercises', 'Guided meditation',
  'New Journal Entry', 'Title', 'How are you feeling?', 'Write your thoughts...',
  'Save Entry', 'Your journal is empty', 'Start writing to track your thoughts and growth.',
  // Notifications
  'Notification Settings', 'Stay in the loop',
  'Choose how you want to be notified about new content, community updates, and wellness reminders.',
  'Email notifications', 'SMS notifications', 'Newsletter', 'Save preferences',
  // AI Guide
  'Your Personal Guide', 'AI companion that knows your assessment results and helps you grow',
  'Ask your guide anything...',
  // Session Brief
  'Session Preparation Brief', 'Generate a brief for your therapist',
  'Generate Brief', 'Share Brief', 'Download',
  'Prepare for your session', 'Generate an AI-powered summary of your journey.',
  'Share with a therapist', 'Select a therapist', 'Add a note (optional)',
  'Share this brief', 'Brief shared successfully',
  // Therapy Chat
  'AI Therapeutic Chat', 'New Session', 'Previous Sessions',
  'This is a supportive space.', 'Type your message...',
  // Name Analysis
  'Name Numerology', 'Discover the hidden meaning in names',
  'Enter a name to analyze', 'Analyze', 'Analyzing...', 'Previous analyses',
  // Rankings
  'Your Rankings', 'See where you stand across all dimensions.',
  'Complete at least one assessment to see your rankings.',
  // Language feature
  'App Language', 'Choose your language', 'The entire app will be translated to your selected language.',
  'Translating...', 'Language updated',
  // Landing page
  'Know yourself.', 'Find your people.', 'Stay safe.', 'Safe space active',
  'A community built on science, unity, and protection. For everyone who wants to understand themselves more deeply — and connect with others who believe every human being deserves dignity.',
  "Begin your journey — it's free", 'See what we stand for', 'Join free',
  'members', 'assessments taken', 'values-verified',
  'Sahiri Amali — a safe friend for self-discovery, backed by real science. If you believe in the dignity of every human being — this is for you.',
  'A safe space for self-discovery',
  'Science-Backed Assessments', 'AI-Powered Insights', 'Private & Intimate', 'Supportive Community',
  // Values Agreement page
  'Our Community Values', 'Please accept all community values', 'Welcome to the community', 'Could not accept values',
  'This platform exists to protect and uplift marginalized communities. Before you can access any features, you must read and accept every one of these values. This is non-negotiable.',
  'Violating these values will result in immediate and permanent removal from the platform. We use AI moderation and community reporting to enforce these standards.',
  'I accept all values', 'Joining...', 'I stand by these values — let me in',
  'By accepting, you agree that your account will be permanently banned if you violate these values.',
  // Pre-auth values page
  'Before You Join', 'Sahiri Amali is a safe space built on shared values. To join our community, you must read and accept each of these principles. This is how we protect each other.',
  'Violating these values will result in immediate and permanent removal from the platform. We take this seriously.',
  'I stand by these values — continue to register',
  'By continuing, you commit to upholding these values. Violations will result in permanent removal.',
  // Community values titles
  'Human Dignity', 'Anti-Racism', 'Anti-Colonialism', 'LGBTQ+ Safety',
  "Women's Autonomy", 'Religious Pluralism', 'Anti-Fascism', 'Protect the Vulnerable',
  // Community values descriptions
  'I believe in the inherent dignity and worth of every human being, regardless of race, ethnicity, gender, sexual orientation, disability, or religion.',
  'I reject racism, white supremacy, and all forms of racial discrimination. I commit to challenging racist systems and supporting racial justice.',
  'I stand against colonialism and its ongoing impacts on Indigenous peoples and communities around the world.',
  'I affirm and protect the rights and safety of LGBTQ+ individuals. I reject homophobia, transphobia, and all forms of anti-LGBTQ+ discrimination.',
  "I respect women's full autonomy over their own bodies, lives, and choices. I reject misogyny and patriarchal control.",
  'I respect religious diversity and reject religious supremacism of all kinds.',
  'I reject fascism, authoritarianism, and political movements that seek to strip rights from marginalized communities.',
  'I commit to protecting the most vulnerable members of our community. I will not harass, stalk, dox, or endanger anyone.',
  // Messaging
  'Messages', 'Find People', 'No conversations yet', 'Type a message...',
  'Connection request sent!', 'Connection accepted!', 'Block', 'Unblock',
  // Announcements
  'Announcements', 'New Announcement', 'Publish',
  // Mental Health
  'Mental Health', 'Your Inner World', 'Refresh',
  // Auth page
  'Create your space', 'Begin exploring who you are', 'Continue your journey of self-discovery',
  'Reset your password', 'Enter your email to receive a reset code', 'Enter the code and your new password',
  'Name', 'Your name', 'Your password', 'Phone Number', 'optional', 'receive a welcome text',
  'Invite Code', 'required', 'Create account', 'Please wait...', 'Forgot your password?',
  'By creating an account, you agree to our', 'Terms of Service', 'Privacy Policy', 'and',
  "Don't have an account?", 'Already have an account?', 'or', 'you@email.com',
  'Knowing yourself is the beginning of all wisdom.',
  'I am a licensed therapist', 'Access the Therapist Dashboard to receive client shares',
  'Which of these is a human emotion?',
  // Human verification options
  'Joy', 'Empathy', 'Hope', 'Grief',
  // Welcome Wizard
  'Welcome to Sahiri Amali', "I'm Sahi, your companion on this journey of self-discovery.",
  "First, let's choose your language.", "Let's discover who you are",
  'Science-backed assessments to understand yourself',
  "I'm here 24/7 to talk, reflect, and guide you",
  'A safe community of people on similar journeys',
  'Your data is private and never shared',
  "You'll create an account in the next step",
  "We detected your language:",
  // Onboarding
  'Choose Your Language', 'Search languages...', 'Our Shared Values', 'Accept All Values',
  "Where would you like to begin?", "Here's what's waiting for you", "Let's Begin",
  'Skip for now', 'Your Name Portrait', 'Self-Discovery Assessments', 'Mental Health Landscape',
  'Sahi — Your AI Companion', 'Daily Mood & Reflections', 'Talk to Someone',
  'Discover what your name reveals across numerology, Kabbalah, runes, and more',
  'Personality, love language, attachment style, and clinical tools',
  'DSM-5 insights to understand your patterns and strengths',
  'Available 24/7 for reflection, guidance, and deep conversations',
  'Track how you feel and watch your journey unfold over time',
  'Therapists, life coaches, and friends ready to listen',
  'Take an Assessment', 'Explore Your Name', 'Explore Therapy', 'Join the Community',
  'Find Your People', 'Send a Message',
  'Discover yourself through 12 science-backed assessments',
  'Uncover the hidden layers of your name across traditions',
  'Talk to our AI therapist or find a practitioner',
  'Connect with people who share your values',
  'Discover compatible partners and friends',
  'Connect directly with other community members',
  'Human Dignity', 'Safety First', 'Radical Inclusion', 'Honest Expression',
  'Consent & Boundaries', 'Anti-Oppression', 'Accountability', 'Growth Mindset',
  'Every person deserves respect regardless of identity, background, or beliefs.',
  'We protect each other. No hate speech, harassment, or threats of any kind.',
  'This space welcomes all orientations, identities, abilities, and backgrounds.',
  'Share authentically. Growth requires vulnerability and truth.',
  "Respect others' boundaries. No means no, always.",
  'We actively resist racism, sexism, homophobia, transphobia, and all forms of systemic oppression.',
  'We own our impact. When we cause harm, we repair it.',
  'We are all learning. Mistakes are opportunities, not sentences.',
  'Welcome to Sahiri Amali!',
  // Landing page — purpose section
  'Why we exist',
  'We built this for the people the world keeps failing.',
  'For women silenced by systems designed to control them. For men taught to carry everything alone and never ask for help. For queer people told they don\'t belong. For Black and Brown people exhausted by a world that dehumanizes them daily. For people living under authoritarian regimes who need a place to be real without fear.',
  'For women, men, queer people, survivors — and everyone the world keeps failing',
  'Sahiri Amali is not another app. It\'s a community with a purpose. We use validated psychological science to help you understand yourself — your personality, your emotional patterns, your attachment style, your values. And then we connect you with others who share your depth.',
  'If you believe in the dignity of every human being, you belong here. If you don\'t — you won\'t make it past the door.',
  // Landing page — safety section
  'Your Safety',
  'Built from the ground up to protect you.',
  'Every feature in Sahiri Amali was designed with the most vulnerable communities in mind. This isn\'t an afterthought — it\'s the foundation.',
  'Values Gate', 'Every user must accept 8 non-negotiable community values before accessing anything. Racists, supremacists, and fascists don\'t get in.',
  'AI Moderation', 'Every message in safe spaces is scanned by AI in real-time. Hate speech, dog whistles, and recruitment attempts are blocked instantly.',
  'Panic Button', 'One click instantly redirects to a safe website. Configurable. Sends a silent alert to your trusted emergency contact.',
  'Anonymous Mode', 'Hide your real identity across the entire platform. Connect through a randomly generated alias. Your name is never exposed.',
  'Auto-Purge', 'Set your data to automatically delete after 7, 30, or 90 days. Your messages vanish on your terms.',
  'Community Trust', 'New users earn trust through positive engagement. Established members can vouch for newcomers. Serial offenders are permanently removed.',
  // Landing page — free features
  'Free forever',
  'Everything you need to start understanding yourself',
  'No paywall to know yourself. Seven research-validated assessments, AI-powered insights, community rooms, journaling, and a personal dashboard — all free.',
  '7 Assessments', 'Big Five, Emotional IQ, Attachment Style, Love Languages, Enneagram, Core Values, Wellness',
  'AI Insights', 'Personalized interpretations of your results that help you understand what they truly mean',
  '5 Community Rooms', 'Connect with others on similar journeys. Moderated. Safe. Anonymous if you choose.',
  'Journal & Reflections', 'Track your mood, write daily reflections, and document your growth over time',
  'Big Five', 'Personality', 'Emotional IQ', 'Awareness', 'Attachment', 'Relationships',
  'Love Languages', 'Connection', 'Enneagram', 'Motivations', 'Core Values', 'Principles',
  'Wellness', 'Wellbeing', 'Personalized',
  // Landing page — premium section
  'Ready to go deeper?',
  'When you\'re ready for more, unlock the full power of self-understanding. Five exclusive assessments, AI therapy, private rooms, and partner matching.',
  '5 Exclusive Assessments', 'Stress resilience, conflict style, self-actualization, shadow self, and meaning-making — the deeper dimensions.',
  'AI Therapy Hub', 'An AI therapeutic companion trained in real frameworks. Session briefs. Coaching directories. Available when you need it.',
  'Partner Discovery', 'Science-backed compatibility matching. We compare personality profiles, attachment styles, values, and emotional patterns.',
  '10 Private Community Rooms', 'Deeper discussions in smaller, more intimate spaces. Members-only moderated rooms for specific topics.',
  'Resistance Journal', 'Document your resistance. Export and download your stories. A private archive that no one can take from you.',
  // Landing page — communities welcome
  'All communities are welcome here —', 'if you want unity.',
  'Your race, your gender, your orientation, your religion, your disability, your story — all of it is welcome. We are a space for people who believe in the inherent worth of every human being. If your ideology requires the oppression of others, this is not your space.',
  'Women', 'LGBTQ+', 'BIPOC', 'Immigrants', 'Disabled', 'Neurodivergent',
  'Refugees', 'Survivors', 'Activists', 'Allies', 'Anyone who wants unity',
  // Landing page — community stories
  'Community Stories', 'In their own words',
  'Real stories from real members. Anonymized. Shared with courage.',
  // Landing page — share section
  'Know someone who needs this?',
  'Share Sahiri Amali with someone who could use a safe space. It might be exactly what they need right now.',
  'Copied!', 'Copy link',
  // Landing page — languages bar
  'Available in', '31 languages', 'with automatic translation',
  // Landing page — final CTA
  'You deserve a space that has your back.',
  'Understand yourself through real science. Connect with people who share your values. Stay safe in a world that doesn\'t always protect you.',
  'Create your free account',
  'Free forever. No credit card. Paid plans available when you\'re ready.',
  // Landing page — footer
  'A community with a purpose', 'Community Impact', 'Find a Practitioner',
  'Terms & Privacy', 'Contact Us', 'Protected',
  // Admin setup
  'Admin Setup', 'Set up your platform administrator account',
  'Admin Already Configured', 'Go to Dashboard',
  'Reset Password', 'Reset your password using the setup key',
  'Become Admin', 'Setting up...', 'Forgot your password?',
  "You're now the admin!", 'Redirecting to Admin Panel...',
  'An admin already exists. Enter your credentials and setup key to claim admin access.',
  'Sign in with your existing account to become the platform admin. This can only be done once.',
  'Setup Key', 'This key is set in your server environment (ADMIN_SETUP_KEY)',
  'Enter your email, a new password, and the setup key to reset your password.',
  'New Password', 'Resetting...', 'Back to Admin Setup',
  'Password reset successfully! Now sign in with your new password below.',
  'This platform already has an admin. If you need admin access, ask the current admin to promote you from the Admin Panel.',
  // Legal page
  'Our Promise', 'Your Data, Your Rights', 'Last updated: March 2026',
  'Sahiri Amali exists to protect and empower marginalized communities. These terms reflect our values: dignity, safety, and transparency. We hold ourselves and our community to these standards.',
  'We collect only what we need, protect it fiercely, and give you full control. You can export or delete all your data at any time. We never sell your information. Your safety is our priority.',
  'Questions about these policies? Reach out through the Sahi assistant — she can help explain anything here.',
  // PreAuthValues language selector
  'Choose your language first',
];

const CACHE_KEY_PREFIX = 'sahiri_translations_';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const CACHE_LANG_KEY = 'innerscope_ui_lang';
const LOCALE_DETECTED_KEY = 'innerscope_locale_detected';

function getCacheKey(lang) {
  return `${CACHE_KEY_PREFIX}${lang}`;
}

// Map browser locale to our supported language codes
function detectBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const code = browserLang.split('-')[0].toLowerCase(); // "es-MX" -> "es"
  return code;
}

export function TranslationProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // 1. If user previously chose a language, use that
    const saved = localStorage.getItem(CACHE_LANG_KEY);
    if (saved) return saved;
    // 2. First visit — detect browser locale (will validate against supported list once loaded)
    return 'en';
  });
  const [translations, setTranslations] = useState(() => {
    try {
      const saved = localStorage.getItem(CACHE_LANG_KEY);
      const lang = saved || 'en';
      if (lang === 'en') return {};
      const cached = localStorage.getItem(getCacheKey(lang));
      if (cached) return JSON.parse(cached);
    } catch (e) {
      console.warn('TranslationContext: could not read cached translations', e);
    }
    return {};
  });
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const fetchedLang = useRef(null);

  // Fetch available languages list + auto-detect on first visit
  useEffect(() => {
    axios.get(`${API_URL}/api/languages`).then(res => {
      setLanguages(res.data);
      // Auto-detect browser language on first visit
      const alreadyDetected = localStorage.getItem(LOCALE_DETECTED_KEY);
      const hasSavedLang = localStorage.getItem(CACHE_LANG_KEY);
      if (!alreadyDetected && !hasSavedLang) {
        const browserCode = detectBrowserLanguage();
        const supported = res.data.find(l => l.code === browserCode);
        if (supported && browserCode !== 'en') {
          setLanguageState(browserCode);
          localStorage.setItem(CACHE_LANG_KEY, browserCode);
          // Persist to backend silently — uses the shared `api` client so the
          // request is authenticated via httpOnly cookie (or in-memory token).
          // Best-effort: backend responds 401 silently when not logged in.
          api.post('/user/language', { language: browserCode }).catch(() => {});
        }
        localStorage.setItem(LOCALE_DETECTED_KEY, 'true');
      }
    }).catch((e) => console.warn('TranslationContext: locale detection failed', e));
  }, []);

  const fetchTranslations = useCallback(async (lang) => {
    if (lang === 'en') {
      setTranslations({});
      fetchedLang.current = 'en';
      return;
    }
    
    // Check per-language cache first — a cache younger than CACHE_TTL_MS is used as-is (no refetch on every page load;
    // strings missing from it are still translated on demand by t()).
    try {
      const cached = localStorage.getItem(getCacheKey(lang));
      if (cached && fetchedLang.current !== lang) {
        setTranslations(JSON.parse(cached));
      }
      const stamp = Number(localStorage.getItem(`${getCacheKey(lang)}_ts`) || 0);
      if (cached && Date.now() - stamp < CACHE_TTL_MS) { fetchedLang.current = lang; return; }
    } catch (e) {
      console.warn('TranslationContext: could not hydrate translation cache', e);
    }

    // Stale or missing cache → fetch every chunk in parallel (one round-trip instead of a sequential chain)
    setLoading(true);
    try {
      const chunks = [];
      for (let i = 0; i < UI_STRINGS.length; i += 60) chunks.push(UI_STRINGS.slice(i, i + 60));
      const results = await Promise.all(chunks.map((chunk) => axios.post(`${API_URL}/api/translations/ui`, { language: lang, texts: chunk })));
      const allTranslations = Object.assign({}, ...results.map((r) => r.data.translations));
      setTranslations(allTranslations);
      localStorage.setItem(getCacheKey(lang), JSON.stringify(allTranslations));
      localStorage.setItem(`${getCacheKey(lang)}_ts`, String(Date.now()));
      fetchedLang.current = lang;
    } catch (e) {
      console.error('Translation fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync language from the user's profile once they are authenticated (login, register, or a
  // cookie session restored after refresh). Anonymous visitors never hit the endpoint — no 401 noise.
  const authUserId = useAuth()?.user?.user_id;
  useEffect(() => {
    if (!authUserId) return;
    const syncLang = async () => {
      try {
        const res = await api.get('/user/profile');
        const userLang = res.data?.preferred_language;
        // If user has a saved language preference, use it (even if English)
        if (userLang && userLang !== language) {
          setLanguageState(userLang);
          localStorage.setItem(CACHE_LANG_KEY, userLang);
          // Fetch translations for the user's language
          if (userLang !== 'en') {
            fetchTranslations(userLang);
          }
        }
      } catch (e) {
        console.warn('TranslationContext: language sync skipped', e);
      }
    };
    syncLang();
  }, [authUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch translations when language changes
  useEffect(() => {
    fetchTranslations(language);
  }, [language, fetchTranslations]);

  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem(CACHE_LANG_KEY, lang);
    // Persist to backend via the shared `api` client (cookie/token auto-attached).
    // Best-effort: a 401 (logged-out user) is silently ignored — the language
    // preference is still applied locally via CACHE_LANG_KEY.
    try {
      await api.post('/user/language', { language: lang });
    } catch (e) {
      console.warn('TranslationContext: could not persist language preference', e);
    }
  }, []);

  // Queue for on-demand translation of strings not in UI_STRINGS
  const pendingRef = useRef(new Set());
  const pendingTimerRef = useRef(null);

  const flushPending = useCallback(async () => {
    if (pendingRef.current.size === 0 || language === 'en') return;
    const texts = Array.from(pendingRef.current);
    pendingRef.current.clear();
    try {
      // Translate in chunks of 30 (legal content can be long)
      const newTranslations = {};
      for (let i = 0; i < texts.length; i += 30) {
        const chunk = texts.slice(i, i + 30);
        const res = await axios.post(`${API_URL}/api/translations/ui`, { language, texts: chunk });
        Object.assign(newTranslations, res.data.translations);
      }
      if (Object.keys(newTranslations).length > 0) {
        setTranslations(prev => {
          const merged = { ...prev, ...newTranslations };
          localStorage.setItem(getCacheKey(language), JSON.stringify(merged));
          return merged;
        });
      }
    } catch (e) {
      console.error('On-demand translation failed:', e);
    }
  }, [language]);

  // The main translate function
  const t = useCallback((text) => {
    if (!text || language === 'en') return text;
    if (translations[text]) return translations[text];
    // Queue for on-demand translation if not already translated
    if (!pendingRef.current.has(text)) {
      pendingRef.current.add(text);
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = setTimeout(() => flushPending(), 300);
    }
    return text; // Return original while waiting
  }, [language, translations, flushPending]);

  return (
    <TranslationContext.Provider value={{ t, language, setLanguage, languages, loading }}>
      {children}
    </TranslationContext.Provider>
  );
}

export const useTranslation = () => useContext(TranslationContext);
